import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import useDialog from "../../a11y/useDialog";

// The one dialog (foundation kit, styles in src/styles/foundation.css .fn-dlg-*).
// - rendered in a portal on <body>, so its overlay covers the navbar too
// - focus moves inside and stays there; Escape and the 44px close button close it (useDialog)
// - the page behind does not scroll while it is open
// - under 600px it rises from the bottom as a sheet
//
// <Dialog open={open} onClose={close} title="Create your free account" subtitle="...">...</Dialog>
// onClose omitted = a blocking dialog (no close button, Escape and the overlay do nothing).
// role="alertdialog" for confirmations and warnings; size="wide" for forms that need room.

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" aria-hidden="true" focusable="false">
    <path d="M6 6l12 12" />
    <path d="M18 6L6 18" />
  </svg>
);

const Dialog = ({
  open,
  onClose,
  title,
  subtitle,
  eyebrow,
  children,
  role = "dialog",
  size,
  closeLabel = "Close",
  className = "",
  describedBy,
  titleId: titleIdProp,
}) => {
  const autoId = useId();
  const titleId = titleIdProp || `dlg-${autoId.replace(/:/g, "")}`;
  const ref = useDialog(open, onClose || null);

  // The page behind does not scroll while any dialog is open (the class goes when the last one closes).
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    document.body.classList.add("fn-dlg-open");
    return () => {
      if (!document.querySelector(".fn-dlg-overlay")) document.body.classList.remove("fn-dlg-open");
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const onOverlay = (e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

  return createPortal(
    <div className="fn-dlg-overlay" onMouseDown={onOverlay}>
      <div
        ref={ref}
        className={`fn-dlg${size === "wide" ? " fn-dlg-wide" : ""}${className ? ` ${className}` : ""}`}
        role={role}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={describedBy}
        tabIndex={-1}
      >
        <span className="fn-dlg-grab" aria-hidden="true" />
        {(title || onClose) && (
          <div className="fn-dlg-head">
            <div className="fn-dlg-titles">
              {eyebrow && <span className="fn-dlg-eyebrow">{eyebrow}</span>}
              {title && (
                <h2 className="fn-dlg-title" id={titleId}>
                  {title}
                </h2>
              )}
              {subtitle && <p className="fn-dlg-sub">{subtitle}</p>}
            </div>
            {onClose && (
              <button type="button" className="fn-dlg-close" aria-label={closeLabel} onClick={onClose}>
                <CloseIcon />
              </button>
            )}
          </div>
        )}
        <div className="fn-dlg-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default Dialog;
